/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Condition, DataField, DataTypeUtils, operations, Query, QueryGroup } from '@deckplan.io/common/filters';
import { QueryComponent } from './query-component';
import { AlertLevel, AlertsService } from '../alerts/alerts.service';
import { FormControl } from '@angular/forms';
import { debounceTime, filter } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { Gtag } from 'angular-gtag';
import { animate, style, transition, trigger } from '@angular/animations';
import { Theme, ThemeService } from '../theme/theme.service';

@Component({
    selector: 'app-advanced-search',
    templateUrl: './advanced-search.component.html',
    styleUrls: ['./advanced-search.component.scss'],
    animations: [
        trigger(
            'inOutAnimation',
            [
                transition(
                    ':enter',
                    [
                        style({height: 0, opacity: 0}),
                        animate('.5s ease-out',
                            style({height: 176, opacity: 1}))
                    ]
                ),
                transition(
                    ':leave',
                    [
                        style({height: 170, opacity: 1}),
                        animate('.5s ease-in',
                            style({height: 0, opacity: 0}))
                    ]
                )
            ]
        )]

})
export class AdvancedSearchComponent implements OnInit {

    @Input() public columns: DataField[] = [];

    @Output() public newQuery = new EventEmitter<Query>();

    @ViewChild('queryView') public queryView: QueryComponent;

    @Input()
    public query: Query = null;

    @Input()
    public editable = true;

    public showAdvanced = false;

    public simpleSearch: FormControl = new FormControl();

    public themeIsLightColor: boolean;

    constructor(private readonly alertService: AlertsService,
                public readonly authService: AuthService,
                private readonly themeService: ThemeService,
                private readonly gtag: Gtag) {}

    public ngOnInit() {
        this.gtag.event('search_component_loaded', {
            user_id: this.authService.currentUserId,
            event_category: 'search'
        });
        this.showAdvanced = false;

        this.simpleSearch.valueChanges.pipe(
            // eslint-disable-next-line no-magic-numbers
            filter(() => !this.showAdvanced),
            // eslint-disable-next-line no-magic-numbers
            debounceTime(300)
        ).subscribe(term => {
            if (!term.length) {
                this.newQuery.emit(null);
            } else {
                this.gtag.event('simple_search_query', {
                    user_id: this.authService.currentUserId,
                    term,
                    event_category: 'search'
                });
                this.newQuery.emit({dataField: { field: 'name', title: 'Name', dataType: 'string'}, value: term, operation: 'includes'});
            }

        });

        // Initialize our theme color
        this.updateForTheme(this.themeService.theme());

        this.themeService.themeChanges.subscribe(this.updateForTheme.bind(this));
    }

    private updateForTheme(theme: Theme) {
        // We could simply set this to 'theme === Theme.Light' but I want to make it
        // easy to adapt this to additional theme options later.
        if (theme === Theme.Light) {
            this.themeIsLightColor = true;
        } else if (theme === Theme.Dark) {
            this.themeIsLightColor = false;
        }
    }

    public add(operator?: string) {
        if (!operator) {
            this.query = {
                dataField: null,
                operation: null,
                value: ''
            } as Condition;
        } else {
            this.query = {
                operator,
                operands: []
            } as QueryGroup;
        }
    }

    public apply() {
        if (this.queryReady()) {
            const query = this.queryView.toQuery();
            this.gtag.event('advanced_search_query', {
                query,
                user_id: this.authService.currentUserId,
                event_category: 'search'
            });
            this.newQuery.emit(query);
        } else {
            this.alertService.alert('Query not ready!', AlertLevel.InfoFailure);
        }
    }

    public clear() {
        this.showAdvanced = false;
        this.simpleSearch.reset('');
        this.query = null;
        this.newQuery.emit(null);
    }

    private queryReady(): boolean {
        let q = [this.queryView.toQuery()];

        while (q.length) {
            const el = q.shift() as any;
            // eslint-disable-next-line no-magic-numbers
            if (!el || el.dataField && !this.isValidCondition(el) || el.operands && el.operands.length < 2 || !el.dataField && !el.operands) {
                return false;
            }

            if (el.operands) {
                q = q.concat(el.operands);
            }
        }

        return true;
    }

    private isValidCondition(raw: any) {
        return this.columns.includes(raw.dataField) && operations.includes(raw.operation) && raw.value;
    }

    public close() {
        if (this.editable) {
            this.clear();
        }
        this.showAdvanced = false;
    }

}
